/**
 * Input hygiene for the contact form rendered by snippets/contact-form-fields.liquid.
 *
 * - Phone fields accept digits and the usual separators only, filtered as you type.
 *   `pattern` alone does not stop letters being entered, it only fails on submit.
 * - Leading whitespace is stripped as you type, so no field can ever hold nothing
 *   but spaces. Emails reject whitespace outright, single-line inputs also collapse
 *   runs of spaces, and textareas keep their internal spacing and line breaks.
 * - Values are trimmed again on blur and on submit to catch trailing whitespace.
 *
 * Rendered as a hidden element inside the form, so it adds no layout of its own.
 */
if (!customElements.get('contact-form-guard')) {
  const PHONE_ALLOWED = /[^0-9+\-() ]/g;
  const TRIMMABLE = 'input[type="text"], input[type="email"], input[type="tel"], textarea';

  class ContactFormGuard extends HTMLElement {
    connectedCallback() {
      const form = this.closest('form');
      if (!form || this.form === form) return;

      this.form = form;
      this.form.addEventListener('input', this.onInput.bind(this));
      this.form.addEventListener('focusout', this.onFocusOut.bind(this));
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    onInput(event) {
      const field = event.target;
      if (!field.matches || !field.matches(TRIMMABLE)) return;

      const before = field.value;
      let after = before;

      if (field.type === 'tel') {
        after = after.replace(PHONE_ALLOWED, '');
      }

      if (field.type === 'email') {
        // No whitespace is ever valid in an address.
        after = after.replace(/\s+/g, '');
      } else if (field.tagName === 'TEXTAREA') {
        // Allow line breaks and spacing inside the message, just not in front of it.
        after = after.replace(/^\s+/, '');
      } else {
        after = after.replace(/^\s+/, '').replace(/ {2,}/g, ' ');
      }

      if (after === before) return;

      field.value = after;

      // Keep the caret put rather than letting it jump to the end. Some input
      // types (email) do not expose a selection, so only adjust when they do.
      if (typeof field.selectionStart === 'number') {
        const caret = Math.max(0, field.selectionStart - (before.length - after.length));
        field.setSelectionRange(caret, caret);
      }
    }

    onFocusOut(event) {
      const field = event.target;
      if (!field.matches || !field.matches(TRIMMABLE)) return;

      const trimmed = field.value.trim();
      if (trimmed !== field.value) field.value = trimmed;
    }

    onSubmit(event) {
      let firstEmpty = null;

      this.form.querySelectorAll(TRIMMABLE).forEach((field) => {
        field.value = field.value.trim();
        if (field.required && field.value === '' && !firstEmpty) firstEmpty = field;
      });

      if (!firstEmpty) return;

      // Native validation already passed on the untrimmed value, so re-run it.
      event.preventDefault();
      firstEmpty.focus();
      if (typeof firstEmpty.reportValidity === 'function') firstEmpty.reportValidity();
    }
  }

  customElements.define('contact-form-guard', ContactFormGuard);
}
